import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { finalize } from 'rxjs';
import Swal from 'sweetalert2';

import { ChangePasswordService } from '../auth/change-password.service';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent implements OnInit {
  passwordForm!: FormGroup;
  submitted = false;
  isSaving = false;
  isAr = false;

  showOldPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  private isBrowser: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private changePasswordService: ChangePasswordService,
    private transloco: TranslocoService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    const lang = this.isBrowser
      ? localStorage.getItem('language') || this.transloco.getActiveLang()
      : this.transloco.getActiveLang();

    this.isAr = lang === 'ar';

    this.passwordForm = this.formBuilder.group(
      {
        old_password: ['', [Validators.required]],
        new_password: ['', [Validators.required, Validators.minLength(8)]],
        new_password_confirmation: ['', [Validators.required]],
      },
      {
        validators: [this.passwordsMatch, this.newPasswordDifferent],
      }
    );
  }

  togglePassword(field: 'old' | 'new' | 'confirm'): void {
    if (field === 'old') {
      this.showOldPassword = !this.showOldPassword;
      return;
    }

    if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
      return;
    }

    this.showConfirmPassword = !this.showConfirmPassword;
  }

  isInvalid(controlName: string): boolean {
    const control = this.passwordForm.get(controlName);

    return !!(
      control &&
      control.invalid &&
      (control.touched || control.dirty || this.submitted)
    );
  }

  hasFormError(errorName: string): boolean {
    return !!(
      this.passwordForm.hasError(errorName) &&
      (this.passwordForm.dirty || this.passwordForm.touched || this.submitted)
    );
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const value = this.passwordForm.value;

    this.isSaving = true;

    this.changePasswordService
      .resetPassword(
        value.old_password,
        value.new_password,
        value.new_password_confirmation
      )
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.passwordForm.reset();
          this.submitted = false;

          Swal.fire({
            icon: 'success',
            title: this.t('changePassword.successTitle'),
            text: this.t('changePassword.successText'),
            confirmButtonText: this.t('changePassword.ok'),
          });

          this.router.navigate(['/profile']);
        },
        error: (err) => {
          const serverMessage =
            err?.error?.message ||
            err?.error?.error ||
            err?.message ||
            this.t('changePassword.errors.updateFailed');

          Swal.fire({
            icon: 'error',
            title: this.t('changePassword.errors.updateFailed'),
            text: serverMessage,
            confirmButtonText: this.t('changePassword.ok'),
          });
        },
      });
  }

  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const password = group.get('new_password')?.value;
    const confirmation = group.get('new_password_confirmation')?.value;

    if (!password || !confirmation) {
      return null;
    }

    return password === confirmation ? null : { passwordMismatch: true };
  }

  private newPasswordDifferent(group: AbstractControl): ValidationErrors | null {
    const oldPassword = group.get('old_password')?.value;
    const newPassword = group.get('new_password')?.value;

    if (!oldPassword || !newPassword) {
      return null;
    }

    return oldPassword === newPassword ? { passwordSameAsOld: true } : null;
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}
